import React, { useState, useRef, useEffect } from 'react';
import { Button, ButtonGroup, Badge, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faUpload, 
  faFileAlt,
  faExclamationCircle,
  faEye,
  faSync,
  faInfoCircle,
  faClock,
  faBan,
  faUndo
} from '@fortawesome/free-solid-svg-icons';

interface DocumentItem {
  id: string;
  name: string;
  type: string;
  reason?: string;
  timing?: string;
  template_id?: string;
  source?: string;
  priority?: string;
  conditions?: string[];
}

interface DocumentPlaceholderItemProps {
  document: DocumentItem;
  uploadedDocument?: any;
  onUpload: (file: File) => Promise<void>;
  onExempt?: (reason?: string) => Promise<void>;
  isRequired?: boolean;
  transactionId?: string;
}

const DocumentPlaceholderItem: React.FC<DocumentPlaceholderItemProps> = ({ 
  document, 
  uploadedDocument, 
  onUpload,
  onExempt,
  isRequired = false,
  transactionId 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isExempting, setIsExempting] = useState(false);
  const [isExempt, setIsExempt] = useState(uploadedDocument?.status === 'exempt' || !!uploadedDocument?.exemption || false);
  const [exemptionData, setExemptionData] = useState(uploadedDocument?.exemption || null);
  const [showExemptForm, setShowExemptForm] = useState(false);
  const [exemptReason, setExemptReason] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Update exemption state when uploadedDocument changes
  useEffect(() => {
    if (uploadedDocument) {
      const hasExemption = uploadedDocument.status === 'exempt' || !!uploadedDocument.exemption;
      setIsExempt(hasExemption);
      setExemptionData(uploadedDocument.exemption || null);
      
      console.log('📄 DocumentPlaceholderItem - Exemption state updated:', {
        documentName: document.name,
        hasExemption,
        exemptionData: uploadedDocument.exemption
      });
    }
  }, [uploadedDocument, document.name]);
  
  // Debug log to see what data we're receiving
  if (uploadedDocument) {
    console.log('📄 DocumentPlaceholderItem - Uploaded doc:', {
      checklistName: document.name,
      uploadedName: uploadedDocument.name || uploadedDocument.filename,
      status: uploadedDocument.status,
      hasReviewStatus: !!uploadedDocument.broker_review_status,
      hasVersionType: !!uploadedDocument.latest_version_type,
      allFields: Object.keys(uploadedDocument)
    });
  }
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };
  
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0]);
    }
  };
  
  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);
      console.log('🔄 DocumentPlaceholderItem - Starting file upload:', {
        documentName: document.name,
        fileName: file.name,
        isReplacement: !!uploadedDocument,
        existingDocId: uploadedDocument?.id
      });
      await onUpload(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleViewDocument = () => {
    if (uploadedDocument?.s3_url || uploadedDocument?.view_url) {
      window.open(uploadedDocument.s3_url || uploadedDocument.view_url, '_blank');
    }
  };
  
  const handleReplaceClick = () => {
    console.log('🔄 Replace button clicked for:', document.name);
    console.log('🔄 File input ref exists:', !!fileInputRef.current);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
      console.error('❌ File input ref is null!');
    }
  };
  
  const handleExemptClick = () => {
    setShowExemptForm(true);
  };
  
  const handleExemptSubmit = async () => {
    try {
      setIsExempting(true);
      const finalReason = exemptReason.trim() || 'Not applicable to this transaction type';
      if (onExempt) {
        await onExempt(finalReason);
        setIsExempt(true);
        // Update exemption data to show the reason immediately
        setExemptionData({
          reason: finalReason,
          exempted_by: 'current_user',
          exempted_at: new Date().toISOString()
        });
        setShowExemptForm(false);
        setExemptReason('');
      }
    } catch (error) {
      console.error('Error exempting document:', error);
      alert('Failed to exempt document. Please try again.');
    } finally {
      setIsExempting(false);
    }
  };
  
  const handleExemptCancel = () => {
    setShowExemptForm(false);
    setExemptReason('');
  };
  
  const handleRemoveExemption = async () => {
    try {
      setIsExempting(true);
      // Call parent's exemption handler with null to indicate removal
      if (onExempt) {
        await onExempt(null); // null indicates removal
        setIsExempt(false);
        setExemptionData(null);
      }
    } catch (error) {
      console.error('Error removing exemption:', error);
      alert('Failed to remove exemption. Please try again.');
    } finally {
      setIsExempting(false);
    }
  };
  
  // Helper function to get status badge info
  const getStatusBadgeInfo = () => {
    if (!uploadedDocument) return null;
    
    // Check if document is exempt (highest priority)
    if (uploadedDocument.status === 'exempt' || uploadedDocument.exemption) {
      return null; // Don't show status badge for exempt documents, the exempt badge is shown separately
    }
    
    // Check broker review status (second highest priority)
    if (uploadedDocument.broker_review_status) {
      switch (uploadedDocument.broker_review_status) {
        case 'approved':
          return { bg: 'success', text: 'Broker Approved' };
        case 'needs_revision':
          return { bg: 'warning', text: 'Needs Revision' };
        case 'rejected':
          return { bg: 'danger', text: 'Rejected' };
        case 'pending':
        case 'pending_review':
          return { bg: 'info', text: 'Under Review' };
        default:
          return { bg: 'secondary', text: uploadedDocument.broker_review_status };
      }
    }
    
    // Then check version type
    if (uploadedDocument.latest_version_type) {
      const versionLabel = uploadedDocument.latest_version_number > 1 
        ? `${uploadedDocument.latest_version_type} (v${uploadedDocument.latest_version_number})`
        : uploadedDocument.latest_version_type;
      
      switch (uploadedDocument.latest_version_type) {
        case 'initial':
          return { bg: 'primary', text: 'Initial Upload' };
        case 'revised':
          return { bg: 'info', text: `Revised (v${uploadedDocument.latest_version_number})` };
        case 'broker_signed':
          return { bg: 'success', text: 'Broker Signed' };
        case 'client_signed':
          return { bg: 'success', text: 'Client Signed' };
        case 'fully_executed':
          return { bg: 'success', text: 'Fully Executed' };
        default:
          return { bg: 'secondary', text: versionLabel };
      }
    }
    
    // Fallback to simple uploaded status
    return { bg: 'success', text: 'Uploaded' };
  };

  // Handle exempt documents without files first
  if (uploadedDocument && (uploadedDocument.status === 'exempt' || uploadedDocument.exemption) && !uploadedDocument.filename && !uploadedDocument.s3_url) {
    return (
      <div className="list-group-item">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={faBan}
            className="text-secondary me-3"
            size="lg"
          />
          <div className="flex-grow-1">
            <div className="d-flex align-items-center">
              <h6 className="mb-0 text-decoration-line-through text-muted">{document.name}</h6>
              <Badge bg="secondary" className="ms-2">
                <FontAwesomeIcon icon={faBan} className="me-1" />
                Exempt
              </Badge>
            </div>
            {uploadedDocument.exemption?.reason && (
              <small className="text-muted d-block mt-1">
                <FontAwesomeIcon icon={faBan} className="me-1" />
                Exemption reason: {uploadedDocument.exemption.reason}
              </small>
            )}
          </div>
          <Button
            variant="outline-info"
            size="sm"
            onClick={handleRemoveExemption}
            disabled={isExempting}
            title="Remove exemption"
          >
            <FontAwesomeIcon icon={faUndo} className="me-2" />
            Remove Exemption
          </Button>
        </div>
      </div>
    );
  }
  
  // Render uploaded state
  if (uploadedDocument) {
    const statusBadge = getStatusBadgeInfo();
    const needsAction = uploadedDocument.broker_review_status === 'needs_revision';
    
    return (
      <div className="list-group-item">
        {/* Hidden file input for replacements */}
        <input
          ref={fileInputRef}
          type="file"
          id={`replace-${document.id}`}
          className="d-none"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          disabled={isUploading}
        />
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={
              isExempt ? faBan :
              uploadedDocument.broker_review_status === 'approved' ? faCheckCircle :
              uploadedDocument.broker_review_status === 'needs_revision' ? faExclamationCircle :
              uploadedDocument.broker_review_status === 'rejected' ? faExclamationCircle :
              faCheckCircle
            }
            className={
              isExempt ? 'text-secondary me-3' :
              uploadedDocument.broker_review_status === 'approved' ? 'text-success me-3' :
              uploadedDocument.broker_review_status === 'needs_revision' ? 'text-warning me-3' :
              uploadedDocument.broker_review_status === 'rejected' ? 'text-danger me-3' :
              'text-success me-3'
            }
            size="lg"
          />
          <div className="flex-grow-1">
            <div className="d-flex align-items-center">
              <h6 className={`mb-0 ${isExempt ? 'text-decoration-line-through text-muted' : ''}`}>{document.name}</h6>
              {isRequired && (
                <Badge bg="danger" className="ms-2">Required</Badge>
              )}
              {statusBadge && (
                <Badge bg={statusBadge.bg} className="ms-2">{statusBadge.text}</Badge>
              )}
              {uploadedDocument.broker_compliance_score && (
                <Badge bg="light" text="dark" className="ms-2">
                  Score: {uploadedDocument.broker_compliance_score}%
                </Badge>
              )}
              {isExempt && (
                <Badge bg="secondary" className="ms-2">
                  <FontAwesomeIcon icon={faBan} className="me-1" />
                  Exempt
                </Badge>
              )}
            </div>
            <small className="text-muted d-block">
              <FontAwesomeIcon icon={faFileAlt} className="me-1" />
              {uploadedDocument.original_filename || uploadedDocument.filename || 'Document'}
            </small>
            {uploadedDocument.broker_reviewed_at && (
              <small className="text-muted d-block">
                Reviewed on {new Date(uploadedDocument.broker_reviewed_at).toLocaleDateString()}
              </small>
            )}
            {needsAction && (
              <small className="text-warning d-block mt-1">
                <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
                Broker has requested revisions. Please upload an updated version.
              </small>
            )}
            {/* Show exemption reason for uploaded documents */}
            {isExempt && exemptionData?.reason && (
              <small className="text-muted d-block mt-1">
                <FontAwesomeIcon icon={faBan} className="me-1" />
                Exemption reason: {exemptionData.reason}
              </small>
            )}
          </div>
          {isExempt ? (
            <Button
              variant="outline-info"
              size="sm"
              onClick={handleRemoveExemption}
              disabled={isExempting}
              title="Remove exemption"
            >
              <FontAwesomeIcon icon={faUndo} className="me-1" />
              Remove Exemption
            </Button>
          ) : (
            <ButtonGroup size="sm">
              <Button 
                variant="outline-primary" 
                onClick={handleViewDocument}
                disabled={!uploadedDocument.s3_url && !uploadedDocument.view_url}
              >
                <FontAwesomeIcon icon={faEye} className="me-1" />
                View
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={handleReplaceClick}
                disabled={isUploading}
              >
                <FontAwesomeIcon icon={faSync} className="me-1" />
                Replace
              </Button>
            </ButtonGroup>
          )}
        </div>
      </div>
    );
  }
  
  // Render empty/upload state
  return (
    <div 
      className={`list-group-item ${isDragging ? 'bg-primary bg-opacity-10' : ''} ${isExempt ? 'opacity-50' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ backgroundColor: isExempt ? '#f8f9fa' : undefined }}
    >
      <div className="d-flex align-items-center">
        <div 
          className={`
            rounded-circle d-flex align-items-center justify-content-center me-3
            ${isRequired ? 'bg-danger bg-opacity-10' : 'bg-secondary bg-opacity-10'}
          `}
          style={{ width: '40px', height: '40px' }}
        >
          <FontAwesomeIcon 
            icon={isRequired ? faExclamationCircle : faFileAlt} 
            className={isRequired ? 'text-danger' : 'text-secondary'}
          />
        </div>
        <div className="flex-grow-1">
          <div className="d-flex align-items-center">
            <h6 className={`mb-0 ${isExempt ? 'text-decoration-line-through text-muted' : ''}`}>{document.name}</h6>
            {isRequired && !isExempt && (
              <Badge bg="danger" className="ms-2">Required</Badge>
            )}
            {isExempt && (
              <Badge bg="secondary" className="ms-2">
                <FontAwesomeIcon icon={faBan} className="me-1" />
                Exempt
              </Badge>
            )}
          </div>
          {/* Always show requirement info */}
          {document.reason && (
            <small className="text-muted d-block">
              {document.reason}
            </small>
          )}
          {document.timing && (
            <small className="text-info d-block">
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
              Timing: {document.timing}
            </small>
          )}
          
          {/* Status is now shown in the button */}
          {isExempt && exemptionData?.reason && (
            <small className="text-muted d-block mt-1">
              <FontAwesomeIcon icon={faBan} className="me-1" />
              Exemption reason: {exemptionData.reason}
            </small>
          )}
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            id={`upload-${document.id}`}
            className="d-none"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            disabled={isUploading}
          />
          <div className="d-flex gap-2">
            <Button
            variant={
              uploadedDocument ? (
                // Check if document has review status (enhanced fields)
                uploadedDocument.broker_review_status ? (
                  uploadedDocument.broker_review_status === 'approved' ? 'success' :
                  uploadedDocument.broker_review_status === 'needs_revision' ? 'danger' :
                  uploadedDocument.broker_review_status === 'rejected' ? 'danger' :
                  'warning'
                ) : (
                  // Fallback to simple status field
                  uploadedDocument.status === 'uploaded' || uploadedDocument.status === 'completed' ? 'warning' : 'secondary'
                )
              ) : (
                isRequired ? 'danger' : 'outline-primary'
              )
            }
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <FontAwesomeIcon icon={faSync} spin className="me-2" />
                Uploading...
              </>
            ) : uploadedDocument ? (
              <>
                {/* Check if document has enhanced review status */}
                {uploadedDocument.broker_review_status ? (
                  uploadedDocument.broker_review_status === 'approved' ? (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Approved
                    </>
                  ) : uploadedDocument.broker_review_status === 'needs_revision' ? (
                    <>
                      <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                      Revision Needed
                    </>
                  ) : uploadedDocument.broker_review_status === 'rejected' ? (
                    <>
                      <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                      Rejected
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faClock} className="me-2" />
                      Pending Review
                    </>
                  )
                ) : (
                  // Fallback for documents with simple status
                  uploadedDocument.status === 'uploaded' || uploadedDocument.status === 'completed' ? (
                    <>
                      <FontAwesomeIcon icon={faClock} className="me-2" />
                      Pending Review
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Uploaded
                    </>
                  )
                )}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Upload
              </>
            )}
            </Button>
            {isRequired && !uploadedDocument && onExempt && !isExempt && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleExemptClick}
                disabled={isExempting}
                title="Mark this document as not applicable to this transaction"
              >
                <FontAwesomeIcon icon={faBan} className="me-2" />
                Exempt
              </Button>
            )}
            {isExempt && (
              <Button
                variant="outline-info"
                size="sm"
                onClick={handleRemoveExemption}
                disabled={isExempting}
                title="Remove exemption"
              >
                <FontAwesomeIcon icon={faUndo} className="me-2" />
                Remove Exemption
              </Button>
            )}
          </div>
        </div>
      </div>
      {isDragging && (
        <div className="text-center text-primary mt-2">
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Drop file here to upload
        </div>
      )}
      
      {/* Inline exemption form */}
      {showExemptForm && (
        <div className="mt-3 p-3 bg-light rounded border">
          <h6 className="text-muted mb-2">
            <FontAwesomeIcon icon={faBan} className="me-2" />
            Exempt Document
          </h6>
          <Form>
            <Form.Group>
              <Form.Label className="small text-muted">Reason for exemption (optional):</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="e.g., Not applicable to investor transaction"
                  value={exemptReason}
                  onChange={(e) => setExemptReason(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleExemptSubmit();
                    }
                  }}
                />
              </InputGroup>
              <Form.Text className="text-muted">
                This document will be marked as not required for this transaction.
              </Form.Text>
            </Form.Group>
            <div className="d-flex gap-2 mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={handleExemptSubmit}
                disabled={isExempting}
              >
                {isExempting ? (
                  <>
                    <FontAwesomeIcon icon={faSync} spin className="me-2" />
                    Exempting...
                  </>
                ) : (
                  'Confirm Exemption'
                )}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleExemptCancel}
                disabled={isExempting}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

export default DocumentPlaceholderItem;